import { useCallback, useState } from "react";
import { storageGet, storageSet } from "../../../lib/browserStorage";

/**
 * A viewer's on/off view preference (a VIEW toggle in the tool rail, `viewRows.ts`): per browser, remembered across
 * reloads, and off when the store is unreachable or holds anything but `"1"`.
 */
export function readViewPref(key: string): boolean {
  const stored = storageGet(key);
  return stored.ok && stored.value === "1";
}

export function writeViewPref(key: string, on: boolean): void {
  storageSet(key, on ? "1" : "0");
}

/** The storage keys. `lvtt.playGrid` predates the VIEW toggles (`terrain-tile-art` ruling 2's toggle). */
export const VIEW_PREF_KEYS = { grid: "lvtt.playGrid", mapKey: "lvtt.mapKey", terrainMechanics: "lvtt.terrainOverlay" } as const;

export type ViewToggle = { on: boolean; toggle: () => void; set: (on: boolean) => void };

/** One stored view preference as React state. */
export function useViewPref(key: string): ViewToggle {
  const [on, setOn] = useState(() => readViewPref(key));
  const set = useCallback(
    (next: boolean) => {
      writeViewPref(key, next);
      setOn(next);
    },
    [key],
  );
  const toggle = useCallback(() => {
    setOn((was) => {
      writeViewPref(key, !was);
      return !was;
    });
  }, [key]);
  return { on, toggle, set };
}
